import React from 'react'
import {useModal} from '_context'
import {NewsletterModal, SwitchLocaleModal, LocaleModal, WishlistSizeModal} from '_uiComponents'
import {useIntl} from 'react-intl'

/**
 * Interface for newsletter modal data
 * @interface NewsletterData
 * @property {string} [newsletterValue] - Optional pre-filled value for the newsletter input
 * @property {string} analyticsLabel - Label used for analytics tracking
 * @property {Function} [analyticsEventNewsletter] - Optional callback function for newsletter analytics events
 */
interface NewsletterData {
    newsletterValue?: string
    analyticsLabel: string
    analyticsEventNewsletter?: (event: {action: string; label: string}) => void
}

/**
 * Interface for switch locale modal data
 * @interface SwitchLocaleData
 * @property {Object} labels - Text and image content for the modal
 * @property {string} labels.title - Modal title
 * @property {string} labels.description - Modal description
 * @property {Object} labels.imageUrl - Responsive image URLs
 * @property {Object} locale - Locale configuration data
 * @property {Array<{label: string, value: string}>} locale.countries - List of available countries
 * @property {{label: string, value: string}} locale.suggested - Suggested locale
 * @property {{label: string, value: string}} locale.current - Current locale
 * @property {Function} [onClose] - Optional callback function when modal closes
 */
interface SwitchLocaleData {
    labels: {
        title: string
        description: string
        imageUrl: {
            desktop: string
            tablet: string
            mobile: string
        }
    }
    locale: {
        countries: {label: string; value: string}[]
        suggested: {label: string; value: string}
        current: {label: string; value: string}
    }
    onClose?: () => void
}
/**
 * Interface for locale modal data
 * @interface LocaleData
 * @property {Function} [onClose] - Optional callback function when modal closes
 */
interface LocaleData {
    onClose?: () => void
}

/**
 * Interface for wishlist size modal data
 * @interface WishlistSizeData
 * @property {boolean} shouldOpenMiniCartOnClose - Flag to determine if mini cart should open after modal closes
 */
interface WishlistSizeData {
    shouldOpenMiniCartOnClose: boolean
}

/**
 * Custom hook for managing different types of modals
 * @returns {Object} Object containing functions to open different types of modals
 * @property {(data: NewsletterData) => void} openNewsletterModal - Opens the newsletter subscription modal
 * @property {(data: SwitchLocaleData) => void} openSwitchLocaleModal - Opens the locale switching modal
 * @property {(data: LocaleData) => void} openLocaleModal - Opens the locale selection modal
 * @property {(data: WishlistSizeData) => void} openWishlistSize - Opens the wishlist size selection modal
 */
export const useOpenModal = () => {
    const {openModal} = useModal()
    const {formatMessage} = useIntl()

    /**
     * Opens the newsletter subscription modal
     * @param {NewsletterData} data - Data required for the newsletter modal
     */
    const openNewsletterModal = (data: NewsletterData) => {
        openModal({
            title: formatMessage({id: 'label.newsletter', defaultMessage: 'Newsletter'}),
            imageUrl: '',
            variant: 'NewsletterModal',
            body: <NewsletterModal emailInputFooter={data.newsletterValue} analyticsLabel={data.analyticsLabel} />,
            onClose: () => {
                data.analyticsEventNewsletter?.({action: 'dismiss', label: data.analyticsLabel})
            },
            className: 'cc-pd-overlay-newsletter',
        })
    }

    /**
     * Opens the locale switching modal
     * @param {SwitchLocaleData} data - Data required for the switch locale modal
     */
    const openSwitchLocaleModal = (data: SwitchLocaleData) => {
        openModal({
            body: <SwitchLocaleModal labels={data.labels} locale={data.locale} />,
            imageUrl: '',
            title: data.labels.title,
            variant: 'switchLocale',
            onClose: data.onClose,
        })
    }

    /**
     * Opens the locale selection modal
     * @param {LocaleData} data - Data required for the locale modal
     */
    const openLocaleModal = (data: LocaleData) => {
        openModal({
            body: (
                <LocaleModal
                    title={formatMessage({id: 'label.form.title', defaultMessage: 'Cambia Paese/Lingua'})}
                    description={formatMessage({
                        id: 'label.form.desc',
                        defaultMessage:
                            'Ti ricordiamo che il catalogo, i servizi e i prezzi possono cambiare in base al Paese selezionato. Eventuali prodotti nel carrello verranno rimossi.',
                    })}
                />
            ),
            imageUrl: '',
            title: formatMessage({id: 'label.form.title', defaultMessage: 'Cambia Paese/Lingua'}),
            variant: 'changeLocale',
            onClose: data.onClose,
        })
    }

    /**
     * Opens the wishlist size selection modal
     * @param {WishlistSizeData} data - Data required for the wishlist size modal
     */
    const openWishlistSize = (data: WishlistSizeData) => {
        openModal({
            body: <WishlistSizeModal shouldOpenMiniCartOnClose={data.shouldOpenMiniCartOnClose} />,
            variant: 'wishlistSize',
            closingIconSize: 'lg',
        })
    }

    return {openNewsletterModal, openSwitchLocaleModal, openLocaleModal, openWishlistSize}
}
